import React, { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { alignSentences, radiusItemFor } from './radiusAlign';
import type { RadiusSegment } from './radiusAlign';
import { MARK_ATTR, POPOVER_ATTR, useRadius } from './RadiusContext';
import { radiusLevelMeta, radiusMethodFor } from './radiusTypes';
import type { RadiusSentence, RadiusThresholds } from './radiusTypes';

/* Renders generated prose with its source attribution laid over the top.
   The text passed in is always rendered verbatim — see radiusAlign.ts. */

export type RadiusTone = 'light' | 'dark';

const POPOVER_WIDTH = 380;
const POPOVER_GAP = 8;
const VIEWPORT_MARGIN = 16;

/** Underline alpha, then the fuller wash used on hover/open. */
const UNDERLINE_ALPHA = { light: 0.28, dark: 0.42 };
const WASH_ALPHA = { light: 0.15, dark: 0.22 };

const withAlpha = (hex: string, alpha: number): string => {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  const int = parseInt(full, 16);
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
};

const quoted = (backing: string): string => backing.replace(/^["']|["']$/g, '');

/** Where the clicked sentence sat, in page coordinates, captured at click time. */
export interface AnchorPoint {
  bottom: number;
  left: number;
}

interface SourcePopoverProps {
  anchor: AnchorPoint;
  sentence: RadiusSentence;
  thresholds?: RadiusThresholds;
  onClose: () => void;
}

const SourcePopover: React.FC<SourcePopoverProps> = ({ anchor, sentence, thresholds, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const meta = radiusLevelMeta(sentence.radius);

  useLayoutEffect(() => {
    // Measured after mount so the real width is known before clamping.
    const width = ref.current?.offsetWidth || POPOVER_WIDTH;
    // The popover is absolutely positioned in PAGE coordinates, so the visible
    // window is offset by the current horizontal scroll — this report can overflow
    // sideways on a narrow screen, and clamping to a bare 0..clientWidth range
    // would park the popover off the left edge whenever the page is scrolled right.
    const minLeft = window.scrollX + VIEWPORT_MARGIN;
    const maxLeft = window.scrollX + document.documentElement.clientWidth - width - VIEWPORT_MARGIN;
    setPosition({
      top: anchor.bottom + POPOVER_GAP,
      left: Math.min(Math.max(anchor.left, minLeft), Math.max(maxLeft, minLeft)),
    });
  }, [anchor]);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="Where this sentence came from"
      {...{ [POPOVER_ATTR]: '' }}
      className="absolute z-[60] rounded-[10px] border border-gray-200 bg-white px-[18px] py-4 shadow-[0_14px_40px_rgba(17,17,17,0.14)]"
      style={{
        width: `min(${POPOVER_WIDTH}px, calc(100vw - ${VIEWPORT_MARGIN * 2}px))`,
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-3 top-2.5 border-0 bg-transparent p-0.5 text-[15px] leading-none text-gray-400 hover:text-gray-600"
      >
        ×
      </button>
      <div
        className="mb-2 text-[9.5px] font-semibold uppercase tracking-[0.09em]"
        style={{ color: meta.color }}
      >
        {meta.label}
      </div>
      {sentence.radius === 0 && sentence.backing ? (
        <blockquote className="mb-2.5 text-[12.5px] italic leading-[1.65] text-gray-900">
          “{quoted(sentence.backing)}”
        </blockquote>
      ) : null}
      {sentence.radius !== 0 && sentence.backing ? (
        <div className="mb-2.5 text-[12.5px] leading-[1.65] text-gray-500">{sentence.backing}</div>
      ) : null}
      <div className="text-[10.5px] tracking-[0.04em] text-gray-400">
        {radiusMethodFor(sentence.radius, thresholds)}
      </div>
    </div>,
    document.body,
  );
};

interface RadiusMarkProps {
  id: string;
  segment: RadiusSegment;
  sentence: RadiusSentence;
  tone: RadiusTone;
  interactive: boolean;
  thresholds?: RadiusThresholds;
}

const RadiusMark: React.FC<RadiusMarkProps> = ({
  id,
  segment,
  sentence,
  tone,
  interactive,
  thresholds,
}) => {
  const { openId, toggle, close } = useRadius();
  const ref = useRef<HTMLSpanElement>(null);
  const [hovered, setHovered] = useState(false);
  const [anchor, setAnchor] = useState<AnchorPoint | null>(null);

  const meta = radiusLevelMeta(sentence.radius);
  const hue = tone === 'dark' ? meta.colorOnDark : meta.color;
  const isOpen = interactive && openId === id;
  const lit = isOpen || hovered;

  const background = lit
    ? `linear-gradient(${withAlpha(hue, WASH_ALPHA[tone])}, ${withAlpha(hue, WASH_ALPHA[tone])})`
    : `linear-gradient(transparent 60%, ${withAlpha(hue, UNDERLINE_ALPHA[tone])} 60%)`;

  // Measure in the event handler, not during render — the popover is positioned
  // from where the sentence was when it was clicked.
  const activate = () => {
    if (!interactive) return;
    const box = ref.current?.getBoundingClientRect();
    if (box) {
      setAnchor({ bottom: box.bottom + window.scrollY, left: box.left + window.scrollX });
    }
    toggle(id);
  };

  return (
    <>
      <span
        ref={ref}
        {...{ [MARK_ATTR]: String(sentence.radius) }}
        role="button"
        tabIndex={0}
        aria-label={`${meta.label}. Show source.`}
        aria-expanded={isOpen}
        onClick={activate}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activate();
          }
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="cursor-pointer rounded-[1px] transition-[background-image] duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#07E5D1]"
        style={{ backgroundImage: background }}
      >
        {segment.text}
      </span>
      {isOpen && anchor ? (
        <SourcePopover
          anchor={anchor}
          sentence={sentence}
          thresholds={thresholds}
          onClose={close}
        />
      ) : null}
    </>
  );
};

export interface RadiusTextProps {
  /** Content-addressed item path, e.g. "verdict" or `insight.${headline}`. */
  path: string;
  text?: string | null;
  /** Text of the element preceding this one under the same path (a card's headline). */
  after?: string;
  tone?: RadiusTone;
  /** Defaults to the provider's print mode; only set to override a single surface. */
  isPrintMode?: boolean;
}

const RadiusText: React.FC<RadiusTextProps> = ({
  path,
  text,
  after,
  tone = 'light',
  isPrintMode,
}) => {
  const { block, enabled, isPrintMode: printFromContext } = useRadius();
  const printing = isPrintMode ?? printFromContext;
  // Per-instance, so marks stay distinct where one item feeds two elements: a card's
  // headline and its body, or the mobile and desktop copies of a segment insight.
  // Keying on the item path alone would make those collide and open together.
  const instanceId = useId();
  const item = enabled ? radiusItemFor(block, path) : null;

  const segments = useMemo(() => {
    if (!item || !text) return null;
    const offset = after ? alignSentences(after, item.sentences).consumed : 0;
    const aligned = alignSentences(text, item.sentences.slice(offset));
    return aligned.segments.some((segment) => segment.sentence) ? aligned.segments : null;
  }, [item, text, after]);

  if (!text) return null;
  if (!segments) return <>{text}</>;

  return (
    <>
      {segments.map((segment, index) =>
        segment.sentence ? (
          <RadiusMark
            key={index}
            id={`${instanceId}#${index}`}
            segment={segment}
            sentence={segment.sentence}
            tone={tone}
            interactive={!printing}
            thresholds={block?.thresholds}
          />
        ) : (
          <React.Fragment key={index}>{segment.text}</React.Fragment>
        ),
      )}
    </>
  );
};

export default RadiusText;
